import {
  Box,
  MenuItem,
  TextField,
  Typography,
  InputAdornment,
  Select,
  Divider,
  styled,
  FormControl as MuiFormControl,
  FormHelperText as MuiFormHelperText,
} from '@mui/material';
import {useFormik} from 'formik';
import {AppState} from '@jobseeker/common-core/types';
import {useSelector} from 'react-redux';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {OtpComponent} from '../OtpComponent';
import {Dispatch, ReactNode, SetStateAction, useEffect, useState} from 'react';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import {validationSchemaPhoneNumber} from '../../../../helpers/formValidator';
import {Image} from '@jobseeker/lotus/components/Image';
import {indianFLag} from '@jobseeker/lotus/assets/images';

interface NumberComponentProps {
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  setTitle: Dispatch<SetStateAction<string>>;
  setSubTitle?: Dispatch<SetStateAction<string>>;
  setDialogContent: Dispatch<SetStateAction<ReactNode>>;
  isForChangeNumber?: boolean;
}

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
  bottom: -18,
}));

const CustomFormControl = styled(MuiFormControl)(({theme}) => ({
  '& .MuiInputBase-root': {
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '& textarea:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '& select:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '&.Mui-error': {
      border: `1px solid ${theme.palette.error.main}`,
      borderRadius: '32px',
    },
  },
}));

const StyledDivider = styled(Divider)(() => ({
  height: '24px',
}));

const StyledTextField = styled(TextField)(() => ({
  borderRadius: '32px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '32px',
    border: '1px solid var(--dividers, rgba(126, 126, 126, 0.20))',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    height: '40px',
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '500',
  },
}));

export const NumberComponent = ({
  setDialogOpen,
  setTitle,
  setSubTitle,
  setDialogContent,
  isForChangeNumber = false,
}: NumberComponentProps) => {
  const {profile} = useSelector((state: AppState) => state.user);

  useEffect(() => {
    if (profile?.countryCode && profile?.phone) {
      formik.setFieldValue('countryCode', 91);
      formik.setFieldValue('phone', profile.phone);
    }
  }, [profile?.countryCode, profile?.phone]);

  const formik = useFormik({
    initialValues: {
      countryCode: 91,
      phone: profile?.phone || '',
      otp: '',
      isForChangeNumber: isForChangeNumber || false,
    },
    validationSchema: validationSchemaPhoneNumber,
    onSubmit: async values => {
      try {
        const response = await userService.twoFaEnable(values);

        if (response?.error) {
          formik.setFieldError('phone', response.error.message);
          return;
        }

        setTitle(
          isForChangeNumber
            ? `Please enter the 6-digit code sent to +${values.countryCode}-${values.phone} to change your mobile number`
            : 'Enable 2-Step Verification'
        );

        if (setSubTitle) {
          setSubTitle(
            isForChangeNumber
              ? ''
              : "Each time you login to your iimjobs.com account, you'll need your password and a verification code sent on this number"
          );
        }

        setDialogContent(
          <OtpComponent
            phone={values.phone}
            countryCode={values.countryCode}
            isForChangeNumber={isForChangeNumber}
            setDialogOpen={setDialogOpen}
          />
        );
      } catch (error) {
        enqueueSnackbar('An error occurred. Please try again.', {
          variant: 'error',
        });
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  const hasError = formik.touched.phone && Boolean(formik.errors.phone);
  const isInputEmpty =
    formik.values.phone.trim() === '' || formik.values.countryCode === 0;

  return (
    <Box>
      {!isForChangeNumber && (
        <Box mt={2}>
          <Typography
            sx={{fontSize: 14, fontWeight: 400, color: 'secondary.main'}}
          >
            {!profile?.phone
              ? 'Add your mobile number'
              : 'Edit your mobile number'}
          </Typography>
          <Typography mt={0.5} sx={{fontSize: 12, fontWeight: 400}}>
            This number will be used for 2-step verification, updated on your
            profile, and shown to recruiters when you apply for jobs
          </Typography>
        </Box>
      )}
      <Box component="form" noValidate onSubmit={formik.handleSubmit}>
        <CustomFormControl sx={{width: '100%', mt: 1.5}}>
          <StyledTextField
            onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
              if (formik.values.phone.length >= 10) {
                event.preventDefault();
              }
            }}
            variant="outlined"
            name="phone"
            autoComplete="off"
            value={formik.values.phone}
            onChange={formik.handleChange}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            placeholder="Enter your Mobile Number"
            sx={{
              input: {
                paddingLeft: 0,
              },
              '.MuiOutlinedInput-root': {
                paddingLeft: 0,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <>
                    <Box
                      width={90}
                      display={'flex'}
                      sx={{padding: '0 16px'}}
                      gap={'12px'}
                      justifyContent={'flex-start'}
                      alignItems={'center'}
                    >
                      <Image
                        src={indianFLag}
                        alt="indianFLag"
                        width={24}
                        height={16}
                      />
                      <Typography
                        variant="subtitle3"
                        color={'#333'}
                        line-height="16px"
                      >
                        +91
                      </Typography>
                    </Box>
                    <StyledDivider orientation="vertical" />
                  </>
                </InputAdornment>
              ),
            }}
          />
          {formik.touched.phone && Boolean(formik.errors.phone) && (
            <FormHelperText error>{formik.errors.phone}</FormHelperText>
          )}
        </CustomFormControl>

        <Box display="flex" gap={1.5} mt={4} mb={2}>
          <Button
            sx={{px: 3, py: 1}}
            disabled={isInputEmpty || hasError}
            type="submit"
            color="secondary"
            variant="contained"
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
